/**
 * ERComposerV1 — first-pass Relay Composer.
 *
 * Per pre-flight aT48UyCnPqQIKWVr1j4i. Spec: multiline textarea (auto-
 * expands to 6 lines then scrolls), tag selector with ≥1 tag required,
 * Shift+Enter or Cmd+Enter sends, Enter inserts newline, localStorage
 * draft persistence, direct Firestore write to team_log_messages.
 *
 * Deliberately NOT in V1: @mentions, #projects, large-content
 * detection, Cmd+K palette, full tag registry integration.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  getDocs,
  limit,
  query,
  where,
} from "firebase/firestore";
import type { User } from "firebase/auth";
import { db } from "../lib/firebase";
import { postWithSequence } from "../lib/teamLog";
import { tagPillStyle } from "../lib/tagColors";
import { parseMentionAt } from "../lib/mentionParser";
import { MentionPicker } from "./MentionPicker";
import { SlashCommandPicker, parseSlashAt } from "./SlashCommandPicker";
import { TagPicker } from "./TagPicker";
import { loadRecentTags, parseTagAt, pushRecentTag, type TagMatch } from "../lib/tagRegistry";
import { slugify, type ProjectRecord } from "../lib/projectsMigration";
import {
  needsProjectTagWarning,
  PROJECT_TAG_WARNING_MESSAGE,
} from "../lib/projectTagWarning";
import {
  likelyCorruptedReservedPrefix,
  findBodyTagTrackMismatch,
  findMissingTrackTagOnGateClearingPost,
  findUnrecognizedGateClearingReply,
  findGateClearingTagWithNoAnchor,
} from "../lib/tagCorruptionWarning";
import { resolveFindingAcknowledgeOutcome, type FindingAcknowledgeOutcome } from "../lib/findingAcknowledgeOutcome";

const COMPOSER_WORKSPACE_ID = "default";
import type { ViewMode } from "../lib/chatFilter";

const COLLECTION = "team_log_messages";
const DRAFT_KEY = "relay_compose_draft";

// Track 10: Tag selection now uses TagPicker component (triggered by # button
// or # keypress) instead of the hardcoded V1_TAGS dropdown. Personal tags
// (notes, reminder, help) are prioritized, followed by recent tags, then full
// Charter vocabulary. Tag usage counts from team_log_messages are displayed.

const MAX_VISIBLE_LINES = 6;
const LINE_HEIGHT_PX = 20;

const RELAY_VERBS = ["status", "history", "find", "save", "summarize"] as const;
type RelayVerb = typeof RELAY_VERBS[number];

function parseRelayCommand(text: string): { verb: RelayVerb; args: string } | null {
  const trimmed = text.trimStart();
  if (!trimmed.toLowerCase().startsWith("@relay ")) return null;
  const rest = trimmed.slice(7).trim();
  const spaceIdx = rest.indexOf(" ");
  const rawVerb = (spaceIdx === -1 ? rest : rest.slice(0, spaceIdx)).toLowerCase();
  if (!(RELAY_VERBS as readonly string[]).includes(rawVerb)) return null;
  return { verb: rawVerb as RelayVerb, args: spaceIdx === -1 ? "" : rest.slice(spaceIdx + 1).trim() };
}

interface DraftShape {
  content: string;
  tags: string[];
}

function readDraft(): DraftShape {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return { content: "", tags: [] };
    const parsed = JSON.parse(raw) as Partial<DraftShape>;
    return {
      content: typeof parsed.content === "string" ? parsed.content : "",
      tags: Array.isArray(parsed.tags) ? parsed.tags.filter(t => typeof t === "string") : [],
    };
  } catch {
    return { content: "", tags: [] };
  }
}

function writeDraft(d: DraftShape): void {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(d));
  } catch {
    // quota exceeded or disabled — non-fatal
  }
}

function clearDraft(): void {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    // non-fatal
  }
}

/**
 * travis@tber.com → "travis". Matches the log's existing author convention
 * (lowercase username, per planner decision 1 in hnMrQ663HC9G2qCtdj7e).
 */
function deriveAuthor(email: string | null): string {
  if (!email) return "unknown";
  const at = email.indexOf("@");
  return (at > 0 ? email.slice(0, at) : email).toLowerCase();
}

export function ERComposerV1({
  user,
  participants = [],
  viewMode = "log",
  onOptimisticInsert,
  onOptimisticRollback,
  workspaceId = "default",
  projects = [],
  activeProjectSlug = null,
  isHumanLead = false,
}: {
  user: User;
  participants?: string[];
  viewMode?: ViewMode;
  // S2-4 PR-3 — optimistic insert callbacks. Composer pre-builds the
  // Firestore doc ref before the write so the optimistic entry shares
  // its id; useTeamLogStream replaces the optimistic entry when the
  // snapshot delivers. Rollback runs on transaction failure.
  onOptimisticInsert?: (msg: import("../types").TeamLogMessage) => void;
  onOptimisticRollback?: (id: string) => void;
  workspaceId?: string;
  // track:track-backlog--cc-composer-project-tag-validator — registered
  // workspace projects (same list that feeds the left nav project
  // selector) + the currently active project slug, used to power the
  // missing-project-tag warning below.
  projects?: ProjectRecord[];
  activeProjectSlug?: string | null;
  // seq #22348 — gates the "directive" tag in TagPicker client-side.
  // Advisory only: server-side assertDirectiveAuthorized (service.ts)
  // is the actual enforcement boundary, this just keeps a non-lead from
  // composing a post that the server will unconditionally reject anyway.
  isHumanLead?: boolean;
}) {
  const initial = useMemo(readDraft, []);
  const [content, setContent] = useState(initial.content);
  const [tags, setTags] = useState<string[]>(initial.tags);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // relay-47 — sequence of the message this draft is replying to. null when
  // not a reply. Set via the `relay:reply-to-message` window event (see
  // useEffect below); cleared after a successful send or via the ✕ chip.
  const [replyToSequence, setReplyToSequence] = useState<number | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // EFISSUE-830 / relay-66 — active @-mention region. Set when the user
  // types `@` in a position that triggers a mention; null otherwise.
  // relay-66 changed selection behavior: `@query` is stripped from the
  // body on pick (was: replaced with `@name `).
  const [mention, setMention] = useState<{ start: number; query: string } | null>(null);

  // relay-66 — active `#` tag region, mirrors `mention`. Mutually
  // exclusive: only one picker is open at a time. When `#` is detected,
  // any open `@` mention is closed and vice versa.
  const [tagMention, setTagMention] = useState<TagMatch | null>(null);

  // relay-66 — recently-used tags surfaced in the picker (workspace-scoped).
  const [recentTags, setRecentTags] = useState<string[]>(() =>
    loadRecentTags(COMPOSER_WORKSPACE_ID),
  );

  // Slash command picker state — active "/" region near cursor.
  const [slashCmd, setSlashCmd] = useState<{ start: number; query: string } | null>(null);

  const author = useMemo(() => deriveAuthor(user.email), [user.email]);

  // track:track-backlog--cc-composer-project-tag-validator — soft warning
  // when a meaningful-type tag is present but no `project:` tag was added.
  // Never blocks Send (canSend stays content-only, below); the human can
  // dismiss and post anyway per the spec's "soft not hard" rationale.
  const showProjectTagWarning = useMemo(() => needsProjectTagWarning(tags), [tags]);
  const suggestedProjectName = useMemo(() => {
    const active = activeProjectSlug ? projects.find(p => slugify(p.name) === activeProjectSlug) : undefined;
    return active?.name ?? projects[0]?.name ?? null;
  }, [projects, activeProjectSlug]);
  const [projectTagWarningDismissed, setProjectTagWarningDismissed] = useState(false);
  useEffect(() => {
    setProjectTagWarningDismissed(false);
  }, [tags.join(" ")]);

  // backlog #46239 — soft, dismissible warnings for the two tag-corruption
  // patterns behind today's incident: a reserved prefix (track:/reply-to:/
  // sprint:/project:/agent:) missing one character, and a body that
  // references a different track: than the one actually tagged. Same "never
  // blocks Send" posture as the project-tag warning above — §34-D's
  // precedent, and the backlog item's own proposal ("a warning the author
  // must dismiss is enough").
  const corruptedTagWarning = useMemo(() => {
    for (const tag of tags) {
      const suspectedPrefix = likelyCorruptedReservedPrefix(tag);
      if (suspectedPrefix) return { tag, suspectedPrefix };
    }
    return null;
  }, [tags]);
  const [corruptedTagWarningDismissed, setCorruptedTagWarningDismissed] = useState(false);
  useEffect(() => {
    setCorruptedTagWarningDismissed(false);
  }, [tags.join(" ")]);

  const trackMismatchWarning = useMemo(() => findBodyTagTrackMismatch(content, tags), [content, tags]);
  const [trackMismatchWarningDismissed, setTrackMismatchWarningDismissed] = useState(false);
  useEffect(() => {
    setTrackMismatchWarningDismissed(false);
  }, [content, tags.join(" ")]);

  // spec #46707 / backlog #46665 — soft, dismissible warning for the third
  // gap: a §3-D gate-clearing tag (pre-flight-approved/finding-acknowledged)
  // posted with no track: tag at all, so no blocked agent can find their own
  // approval by filtering track:. Same "never blocks Send" posture as the
  // two warnings above.
  const missingTrackTagWarning = useMemo(
    () => findMissingTrackTagOnGateClearingPost(tags),
    [tags],
  );
  const [missingTrackTagWarningDismissed, setMissingTrackTagWarningDismissed] = useState(false);
  useEffect(() => {
    setMissingTrackTagWarningDismissed(false);
  }, [tags.join(" ")]);

  // backlog #46961 — soft, dismissible warnings for the two write-time gaps
  // behind three malformed gate-clearing posts in one day: a reply with no
  // recognized unblock tag (#46960, tagged `approved` instead of
  // `pre-flight-approved`), and a recognized unblock tag with no resolvable
  // anchor (#46916, bare numeric tags instead of `reply-to:{seq}`). Same
  // "never blocks Send" posture as the warnings above.
  const unrecognizedGateClearingReplyWarning = useMemo(
    () => findUnrecognizedGateClearingReply(tags, replyToSequence),
    [tags, replyToSequence],
  );
  const [unrecognizedGateClearingReplyWarningDismissed, setUnrecognizedGateClearingReplyWarningDismissed] =
    useState(false);
  useEffect(() => {
    setUnrecognizedGateClearingReplyWarningDismissed(false);
  }, [tags.join(" "), replyToSequence]);

  const gateClearingTagWithNoAnchorWarning = useMemo(
    () => findGateClearingTagWithNoAnchor(tags, replyToSequence),
    [tags, replyToSequence],
  );
  const [gateClearingTagWithNoAnchorWarningDismissed, setGateClearingTagWithNoAnchorWarningDismissed] =
    useState(false);
  useEffect(() => {
    setGateClearingTagWithNoAnchorWarningDismissed(false);
  }, [tags.join(" "), replyToSequence]);

  // backlog #47309 — secondary surface (the primary is ActionsTab.tsx's
  // BlockerResolutionModal): when this post carries finding-acknowledged and
  // replies to a resolvable seq, resolve that seq's content/tags (one-off
  // read, same async-on-tag-change pattern as the project-tag warning above)
  // and predict whether it closes or re-queues the track, same as the
  // Actions-tab card. Non-blocking — informational only.
  const [findingAcknowledgeOutcome, setFindingAcknowledgeOutcome] = useState<FindingAcknowledgeOutcome | null>(null);
  useEffect(() => {
    setFindingAcknowledgeOutcome(null);
    if (!tags.includes("finding-acknowledged") || replyToSequence == null) return;
    let cancelled = false;
    (async () => {
      try {
        const q = query(
          collection(db, COLLECTION),
          where("sequence", "==", replyToSequence),
          where("workspace_id", "==", workspaceId),
          limit(1),
        );
        const snap = await getDocs(q);
        if (cancelled || snap.empty) return;
        const data = snap.docs[0].data();
        setFindingAcknowledgeOutcome(resolveFindingAcknowledgeOutcome(data.tags, data.content));
      } catch (err) {
        console.warn("[ERComposerV1] finding-acknowledge outcome lookup failed (non-fatal):", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tags, replyToSequence, workspaceId]);
  const [findingAcknowledgeOutcomeDismissed, setFindingAcknowledgeOutcomeDismissed] = useState(false);
  useEffect(() => {
    setFindingAcknowledgeOutcomeDismissed(false);
  }, [tags.join(" "), replyToSequence]);

  // Escalation is decided server-side (postMessage → recordProjectTagMiss)
  // and arrives on the POST response; see handleSend.
  const [escalatedProjectTagWarning, setEscalatedProjectTagWarning] = useState<string | null>(null);

  function addSuggestedProjectTag() {
    if (!suggestedProjectName) return;
    const tag = `project:${suggestedProjectName}`;
    setTags(prev => (prev.includes(tag) ? prev : [...prev, tag]));
  }

  // Persist draft on every change. Debounce would be nicer; skipping for V1
  // since the payload is tiny and localStorage writes are synchronous but
  // fast.
  useEffect(() => {
    writeDraft({ content, tags });
  }, [content, tags]);

  // Auto-grow textarea up to MAX_VISIBLE_LINES.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const maxHeight = MAX_VISIBLE_LINES * LINE_HEIGHT_PX + 16; // + vertical padding
    el.style.height = Math.min(el.scrollHeight, maxHeight) + "px";
    el.style.overflowY = el.scrollHeight > maxHeight ? "auto" : "hidden";
  }, [content]);

  // Sprint `IfXyyJg01e1ox2VAIZIU`, Commit B — external focus request.
  // App.tsx dispatches `relay:focus-composer` after scrolling a target
  // message into view (e.g. a reply action) so the user can type without
  // a second click. Decoupled via a custom DOM event so callers don't
  // need a ref handle into the composer.
  useEffect(() => {
    const onFocus = () => textareaRef.current?.focus();
    window.addEventListener("relay:focus-composer", onFocus);
    return () => window.removeEventListener("relay:focus-composer", onFocus);
  }, []);

  // EFISSUE-840 — pre-fill request (e.g. a "Reply" action elsewhere in the
  // app). App.tsx dispatches `relay:prefill-composer` with
  // `{content, tags}` before the focus event so the textarea + tag pills
  // land already populated. Replaces (not appends) current draft state so
  // the user sees a clean draft rather than a merged one.
  useEffect(() => {
    const onPrefill = (e: Event) => {
      const detail = (e as CustomEvent<{ content?: string; tags?: string[] }>).detail;
      if (!detail) return;
      if (typeof detail.content === "string") setContent(detail.content);
      if (Array.isArray(detail.tags)) setTags(detail.tags.filter((t) => typeof t === "string"));
    };
    window.addEventListener("relay:prefill-composer", onPrefill as EventListener);
    return () => window.removeEventListener("relay:prefill-composer", onPrefill as EventListener);
  }, []);

  // relay-47 — Reply intent. App.tsx fires `relay:reply-to-message` with
  // the target sequence when the user clicks a #NNN chip in the stream.
  // We stash the sequence; on send it threads through the Firestore write
  // and the receiver renders a Re: #NNN backreference. Also focuses so
  // the user can type without a second click.
  useEffect(() => {
    const onReplyTo = (e: Event) => {
      const detail = (e as CustomEvent<{ sequence?: number }>).detail;
      if (!detail || typeof detail.sequence !== "number") return;
      setReplyToSequence(detail.sequence);
      textareaRef.current?.focus();
    };
    window.addEventListener("relay:reply-to-message", onReplyTo as EventListener);
    return () => window.removeEventListener("relay:reply-to-message", onReplyTo as EventListener);
  }, []);


  // S2-4 PR-3a (REQ-1, defect #1176) — composer no longer requires the
  // user to pre-select a tag. Send activates as soon as there is text;
  // posts default to `tags: ['chat']` if the user didn't pick anything.
  // Existing behavior is preserved when an explicit tag is selected,
  // when in chat-mode (still appends `chat`), and for `@relay` commands.
  const isChatMode = viewMode === "chat";
  const canSend = content.trim().length > 0 && !sending;

  async function handleSend() {
    if (content.trim().length === 0) return;
    const relayCmd = parseRelayCommand(content);
    // Tag composition order:
    //   1. user-selected tags (could be empty)
    //   2. `chat` appended in chat mode OR when no explicit tag was picked
    //   3. `relay:command` + `relay:cmd-<verb>` appended for @relay commands
    const withChatDefault = (() => {
      const base = (() => {
        if (tags.length === 0) return ["chat"];
        if (isChatMode && !tags.includes("chat")) return [...tags, "chat"];
        return tags;
      })();
      return author === "travis" && !base.includes("travis") ? [...base, "travis"] : base;
    })();
    const effectiveTags = relayCmd
      ? [...new Set([...withChatDefault, "relay:command", `relay:cmd-${relayCmd.verb}`])]
      : withChatDefault;
    setSending(true);
    setError(null);
    // S2-4 PR-3 — optimistic insert under a client-side placeholder id;
    // removed once the server confirms the post (or on failure).
    const optimisticId = `optimistic-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const trimmedContent = content.trim();
    const sendTimestamp = new Date().toISOString();
    const replyTo = replyToSequence ?? null;
    if (onOptimisticInsert) {
      onOptimisticInsert({
        id: optimisticId,
        author,
        content: trimmedContent,
        tags: effectiveTags,
        timestamp: sendTimestamp,
        replyToSequence: replyTo ?? undefined,
      });
    }
    try {
      // SEC-COUNTERS (spec #51075) — the server stamps the sequence; the
      // browser no longer writes relay_counters or the message doc. The
      // server also sets timestamp/processed/workspace_id (from the auth
      // header) and applies the author-binding gate.
      const posted = await postWithSequence(db, {
        author,
        content: trimmedContent,
        tags: effectiveTags,
        // EFISSUE-836 — sentinel the realestateflow `postMessage()` guard
        // checks before letting Travis post a directive-class tag. The
        // composer is the one sanctioned path.
        origin: "relay-composer",
        workspace_id: workspaceId,
        replyToSequence: replyTo ?? undefined,
      });
      // The server assigns the real doc id. Re-key the placeholder to it
      // instead of dropping it, so the message never vanishes before the
      // snapshot listener delivers the stored doc (which then overwrites this
      // entry under the same id).
      if (onOptimisticInsert) {
        onOptimisticInsert({
          id: posted.id,
          author,
          content: trimmedContent,
          tags: effectiveTags,
          timestamp: sendTimestamp,
          sequence: posted.sequence,
          replyToSequence: replyTo ?? undefined,
        });
      }
      onOptimisticRollback?.(optimisticId);
      // The server counts missed project tags (recordProjectTagMiss) and
      // reports escalation via a response header; counting here as well
      // would double-count and escalate at half the intended threshold.
      if (posted.projectTagWarning) {
        setEscalatedProjectTagWarning(PROJECT_TAG_WARNING_MESSAGE);
      }
      setContent("");
      setTags([]);
      setReplyToSequence(null);
      clearDraft();
    } catch (err) {
      // Roll back the optimistic entry so the user doesn't see a ghost
      // message in the chat window when the write fails.
      onOptimisticRollback?.(optimisticId);
      const msg = err instanceof Error ? err.message : "Failed to send";
      setError(msg);
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // Slack-style: Shift+Enter OR Cmd+Enter → send; plain Enter → newline.
    // EFISSUE-830 — when the mention menu is open the menu owns ↑/↓/Enter/
    // Tab/Escape via a window-level capture-phase listener; we still let
    // Shift/Cmd+Enter through to send for power users mid-mention.
    if (e.key === "Enter" && (e.shiftKey || e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSend();
    }
  }

  // EFISSUE-830 / relay-66 — re-parse mention + tag regions whenever
  // content or cursor changes. Triggered from the textarea's onChange +
  // onKeyUp + onClick so we catch arrow-key cursor moves as well as
  // typing. Pickers are mutually exclusive: slash command wins over
  // `@` and `#`; between `@` and `#`, the one closer to the cursor wins.
  function refreshMention() {
    const el = textareaRef.current;
    if (!el) return;
    const cursor = el.selectionStart ?? 0;
    // Slash command takes highest priority.
    const s = parseSlashAt(content, cursor);
    if (s) {
      setSlashCmd(s);
      setMention(null);
      setTagMention(null);
      return;
    }
    setSlashCmd(null);
    const m = parseMentionAt(content, cursor);
    const t = parseTagAt(content, cursor);
    // If both match, the one whose trigger is closer to the cursor wins.
    if (m && t) {
      if (t.start > m.start) {
        setMention(null);
        setTagMention(t);
      } else {
        setTagMention(null);
        setMention(m);
      }
      return;
    }
    setMention(m);
    setTagMention(t);
  }

  // S2-4 spec #1126 — `@<name>` must remain in the message body so the
  // Cloud Function fan-out regex `(?:^|\s)@([A-Za-z0-9_-]+)\b` can fire
  // ACPs to the mentioned participant. Earlier behavior (relay-66)
  // stripped `@query` and added an `agent:<name>` tag only; that broke
  // the new content-based fan-out path. We now resolve the partial
  // `@query` to `@name ` in the body AND keep the `agent:<name>` tag
  // for any feature still keyed on tag-based routing.
  function applyMention(name: string) {
    if (!mention) return;
    const before = content.slice(0, mention.start);
    const after = content.slice(mention.start + 1 + mention.query.length);
    const cleanedAfter = after.startsWith(" ") ? after.slice(1) : after;
    const inserted = `@${name} `;
    const next = `${before}${inserted}${cleanedAfter}`;
    setContent(next);
    setTags((prev) => (prev.includes(`agent:${name}`) ? prev : [...prev, `agent:${name}`]));
    setMention(null);
    const newPos = mention.start + inserted.length;
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(newPos, newPos);
    });
  }

  // relay-66 — apply a tag selection from the `#` picker. Strips the
  // `#query` text from the body and adds the chosen tag to the chip
  // array. Bumps the tag in workspace-scoped recent-tag history.
  function applyTagPick(tag: string) {
    if (!tagMention) return;
    const before = content.slice(0, tagMention.start);
    const after = content.slice(tagMention.start + 1 + tagMention.query.length);
    const cleanedAfter = after.startsWith(" ") ? after.slice(1) : after;
    setContent(`${before}${cleanedAfter}`);
    setTags((prev) => (prev.includes(tag) ? prev : [...prev, tag]));
    pushRecentTag(COMPOSER_WORKSPACE_ID, tag);
    setRecentTags(loadRecentTags(COMPOSER_WORKSPACE_ID));
    setTagMention(null);
    const newPos = tagMention.start;
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(newPos, newPos);
    });
  }

  // Tags auto-applied when certain slash commands are selected.
  // /help maps to relay-help (not bare "help") — matches HelpPanel's tag filter.
  const SLASH_TAG_MAP: Record<string, string> = {
    "/help": "relay-help",
    "/blocker": "blocker",
    "/decision": "decision",
    "/pre-flight": "pre-flight",
    "/status": "status",
  };

  // Slash command selection: replace "/" + query with the chosen command,
  // and auto-apply the associated tag when one exists.
  function applySlashCommand(cmd: string) {
    if (!slashCmd) return;
    const before = content.slice(0, slashCmd.start);
    const after = content.slice(slashCmd.start + 1 + slashCmd.query.length);
    const cleanedAfter = after.startsWith(" ") ? after.slice(1) : after;
    const next = before + cmd + (cleanedAfter ? " " + cleanedAfter : "");
    setContent(next);
    setSlashCmd(null);
    const autoTag = SLASH_TAG_MAP[cmd];
    if (autoTag) {
      setTags(prev => prev.includes(autoTag) ? prev : [...prev, autoTag]);
    }
    const newPos = before.length + cmd.length;
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(newPos, newPos);
    });
  }

  function removeTag(t: string) {
    setTags(prev => prev.filter(x => x !== t));
  }

  function tagStyle(t: string): React.CSSProperties {
    // Shared source of truth with TeamLogStream — see src/lib/tagColors.ts.
    // Keeps the Composer preview pill identical to the posted message pill.
    const s = tagPillStyle(t);
    return { background: s.bg, color: s.color, border: s.border };
  }

  return (
    <div className="er-composer">
      {/* relay-47 — reply-to indicator. Visible when a reply is queued; ✕
          clears it without affecting content/tag draft state. */}
      {replyToSequence != null && (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            marginBottom: 6,
            padding: "2px 8px",
            borderRadius: 12,
            fontSize: 11,
            fontFamily: "var(--font-mono)",
            background: "var(--surface2)",
            color: "var(--text2)",
            border: "1px solid var(--border)",
          }}
        >
          Replying to #{replyToSequence}
          <button
            type="button"
            onClick={() => setReplyToSequence(null)}
            aria-label="Cancel reply"
            title="Cancel reply"
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              cursor: "pointer",
              padding: 0,
              fontSize: 14,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
      )}
      {/* Selected-tag pills (above textarea) */}
      {tags.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {tags.map(t => (
            <span
              key={t}
              style={{
                ...tagStyle(t),
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "2px 8px",
                borderRadius: 12,
                fontSize: 11,
                fontFamily: "var(--font-mono)",
              }}
            >
              {t}
              <button
                type="button"
                onClick={() => removeTag(t)}
                aria-label={`Remove ${t} tag`}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "inherit",
                  cursor: "pointer",
                  padding: 0,
                  fontSize: 14,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* backlog #46239 — soft, dismissible warning for a reserved-prefix
          tag that looks like it lost a character (e.g. "rack:" instead of
          "track:"). Same posture as the project-tag warning below: never
          blocks Send. */}
      {corruptedTagWarning && !corruptedTagWarningDismissed && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 8,
            padding: "6px 10px",
            borderRadius: 8,
            fontSize: 11,
            fontFamily: "var(--font-ui)",
            background: "#FDECEA",
            border: "1px solid #E8A7A0",
            color: "#7A2E24",
          }}
        >
          <span style={{ flex: 1 }}>
            <code>{corruptedTagWarning.tag}</code> looks like it's missing a character — did you mean{" "}
            <code>{corruptedTagWarning.suspectedPrefix}...</code>? A corrupted tag won't clear a §3-D/§5
            gate even though the post looks fine.
          </span>
          <button
            type="button"
            onClick={() => setCorruptedTagWarningDismissed(true)}
            aria-label="Dismiss tag corruption warning"
            title="Dismiss"
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              cursor: "pointer",
              padding: 0,
              fontSize: 14,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* backlog #46239 — soft, dismissible warning when the body text
          references a different track: than the one actually tagged
          (catches #46233's near-miss: body authorized one track, tag named
          another). Never blocks Send. */}
      {trackMismatchWarning && !trackMismatchWarningDismissed && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 8,
            padding: "6px 10px",
            borderRadius: 8,
            fontSize: 11,
            fontFamily: "var(--font-ui)",
            background: "#FDECEA",
            border: "1px solid #E8A7A0",
            color: "#7A2E24",
          }}
        >
          <span style={{ flex: 1 }}>
            Your message mentions <code>track:{trackMismatchWarning.bodyTrackId}</code> but the tag says{" "}
            <code>track:{trackMismatchWarning.tagTrackId}</code>. Only the tag decides which track this
            post gates — double-check before sending.
          </span>
          <button
            type="button"
            onClick={() => setTrackMismatchWarningDismissed(true)}
            aria-label="Dismiss track mismatch warning"
            title="Dismiss"
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              cursor: "pointer",
              padding: 0,
              fontSize: 14,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* spec #46707 / backlog #46665 — soft, dismissible warning when a
          §3-D gate-clearing tag (pre-flight-approved/finding-acknowledged)
          has no track: tag at all. Never blocks Send. */}
      {missingTrackTagWarning && !missingTrackTagWarningDismissed && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 8,
            padding: "6px 10px",
            borderRadius: 8,
            fontSize: 11,
            fontFamily: "var(--font-ui)",
            background: "#FDECEA",
            border: "1px solid #E8A7A0",
            color: "#7A2E24",
          }}
        >
          <span style={{ flex: 1 }}>
            <code>{missingTrackTagWarning.gateClearingTag}</code> has no <code>track:</code> tag — a
            blocked agent won't be able to find this approval by filtering on their track.
          </span>
          <button
            type="button"
            onClick={() => setMissingTrackTagWarningDismissed(true)}
            aria-label="Dismiss missing track tag warning"
            title="Dismiss"
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              cursor: "pointer",
              padding: 0,
              fontSize: 14,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* backlog #46961 — soft, dismissible warning when a reply carries
          none of the tags the scheduler actually checks for to clear a
          §3-D gate (e.g. `approved` instead of `pre-flight-approved`).
          Never blocks Send. */}
      {unrecognizedGateClearingReplyWarning && !unrecognizedGateClearingReplyWarningDismissed && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 8,
            padding: "6px 10px",
            borderRadius: 8,
            fontSize: 11,
            fontFamily: "var(--font-ui)",
            background: "#FDECEA",
            border: "1px solid #E8A7A0",
            color: "#7A2E24",
          }}
        >
          <span style={{ flex: 1 }}>
            This reply doesn't carry a tag the scheduler recognizes for clearing a gate — try{" "}
            {unrecognizedGateClearingReplyWarning.recognizedTags.map((t, i) => (
              <span key={t}>
                {i > 0 ? ", " : ""}
                <code>{t}</code>
              </span>
            ))}
            . Anything else won't clear it, silently.
          </span>
          <button
            type="button"
            onClick={() => setUnrecognizedGateClearingReplyWarningDismissed(true)}
            aria-label="Dismiss unrecognized gate-clearing tag warning"
            title="Dismiss"
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              cursor: "pointer",
              padding: 0,
              fontSize: 14,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* backlog #46961 — soft, dismissible warning when a recognized
          unblock tag has no resolvable anchor (no reply-intent, no
          `reply-to:{seq}` tag — a bare numeric tag doesn't count). Never
          blocks Send. */}
      {gateClearingTagWithNoAnchorWarning && !gateClearingTagWithNoAnchorWarningDismissed && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 8,
            padding: "6px 10px",
            borderRadius: 8,
            fontSize: 11,
            fontFamily: "var(--font-ui)",
            background: "#FDECEA",
            border: "1px solid #E8A7A0",
            color: "#7A2E24",
          }}
        >
          <span style={{ flex: 1 }}>
            <code>{gateClearingTagWithNoAnchorWarning.tag}</code> has no anchor — click the #NNN
            message you're clearing, or add <code>reply-to:{"{seq}"}</code>, or this won't clear
            anything.
          </span>
          <button
            type="button"
            onClick={() => setGateClearingTagWithNoAnchorWarningDismissed(true)}
            aria-label="Dismiss unanchored gate-clearing tag warning"
            title="Dismiss"
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              cursor: "pointer",
              padding: 0,
              fontSize: 14,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* backlog #47309 — same label treatment as ActionsTab.tsx's
          BlockerResolutionModal: finding-acknowledged has two outcomes
          (close the track, or re-queue it for dispatch) and neither should
          be a silent default. AC4: the re-queue branch additionally notes
          the currently-unguarded re-dispatch (DEC-7; #46664/#47140) —
          remove that clause once #46664 lands. Never blocks Send. */}
      {findingAcknowledgeOutcome && !findingAcknowledgeOutcomeDismissed && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 8,
            padding: "6px 10px",
            borderRadius: 8,
            fontSize: 11,
            fontFamily: "var(--font-ui)",
            background: findingAcknowledgeOutcome.branch === "close" ? "#E8F3EC" : "#FFF6E0",
            border: findingAcknowledgeOutcome.branch === "close" ? "1px solid #A8D5BA" : "1px solid #F0D68A",
            color: findingAcknowledgeOutcome.branch === "close" ? "#1E5B3A" : "#6B5A1E",
          }}
        >
          <span style={{ flex: 1 }}>
            {findingAcknowledgeOutcome.branch === "close" ? (
              <>
                This finding-acknowledged closes the track — investigation-only
                {findingAcknowledgeOutcome.via === "inferred" ? " (inferred, not an explicit tag)" : ""}.
              </>
            ) : (
              <>
                This finding-acknowledged re-queues the track for dispatch — currently unguarded,
                may reassign to a different agent (DEC-7; #46664/#47140 not yet fixed).
              </>
            )}
          </span>
          <button
            type="button"
            onClick={() => setFindingAcknowledgeOutcomeDismissed(true)}
            aria-label="Dismiss finding-acknowledged outcome"
            title="Dismiss"
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              cursor: "pointer",
              padding: 0,
              fontSize: 14,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* track:track-backlog--cc-composer-project-tag-validator — soft,
          dismissible warning. Renders only when a meaningful-type tag
          (backlog/directive/build-complete/finding/pre-flight/blocker/
          sprint-planned) is selected without a project: tag. */}
      {showProjectTagWarning && !projectTagWarningDismissed && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 8,
            padding: "6px 10px",
            borderRadius: 8,
            fontSize: 11,
            fontFamily: "var(--font-ui)",
            background: "#FFF6E0",
            border: "1px solid #F0D68A",
            color: "#6B5A1E",
          }}
        >
          <span style={{ flex: 1 }}>
            Don't forget a project tag.
            {projects.length > 0 && ` Your projects: ${projects.map(p => p.name).join(" · ")}.`}
          </span>
          {suggestedProjectName && (
            <button
              type="button"
              onClick={addSuggestedProjectTag}
              style={{
                background: "#F0D68A",
                border: "none",
                borderRadius: 6,
                padding: "3px 8px",
                fontSize: 11,
                fontWeight: 600,
                color: "#4A3D10",
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              Add project:{suggestedProjectName}
            </button>
          )}
          <button
            type="button"
            onClick={() => setProjectTagWarningDismissed(true)}
            aria-label="Dismiss project tag warning"
            title="Dismiss"
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              cursor: "pointer",
              padding: 0,
              fontSize: 14,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Escalated project-tag warning — shown once an author crosses
          PROJECT_TAG_WARNING_THRESHOLD missed-tag posts (see
          escalatedProjectTagWarning above). Never blocks sending; the post
          that triggered this already went through. */}
      {escalatedProjectTagWarning && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 8,
            padding: "6px 10px",
            borderRadius: 8,
            fontSize: 11,
            fontFamily: "var(--font-ui)",
            background: "#FDECEA",
            border: "1px solid #E8A7A0",
            color: "#7A2E24",
          }}
        >
          <span style={{ flex: 1 }}>{escalatedProjectTagWarning}</span>
          <button
            type="button"
            onClick={() => setEscalatedProjectTagWarning(null)}
            aria-label="Dismiss project tag warning"
            title="Dismiss"
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              cursor: "pointer",
              padding: 0,
              fontSize: 14,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Textarea + tag button + send button row */}
      <div className="er-composer-box" style={{ alignItems: "flex-end" }}>
        {/* Tag picker trigger button */}
        <button
          type="button"
          onClick={() => {
            // Trigger TagPicker by inserting # at cursor position
            const el = textareaRef.current;
            if (!el) return;
            const pos = el.selectionStart ?? 0;
            const needsSpace = pos > 0 && !/\s/.test(content[pos - 1] || "");
            const insertion = needsSpace ? " #" : "#";
            const newContent = content.slice(0, pos) + insertion + content.slice(pos);
            setContent(newContent);
            requestAnimationFrame(() => {
              el.focus();
              const newPos = pos + insertion.length;
              el.setSelectionRange(newPos, newPos);
              refreshMention(); // Triggers TagPicker detection
            });
          }}
          aria-label="Add a tag to your message"
          title="Add a tag to your message"
          style={{
            background: "transparent",
            border: "1px solid var(--border)",
            borderRadius: 6,
            padding: "6px 10px",
            color: "var(--text2)",
            fontSize: 12,
            cursor: "pointer",
          }}
        >
          # tag
        </button>

        <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
          <textarea
            ref={textareaRef}
            value={content}
            onChange={e => { setContent(e.target.value); requestAnimationFrame(refreshMention); }}
            onKeyDown={handleKeyDown}
            onKeyUp={refreshMention}
            onClick={refreshMention}
            placeholder="Post to Team Log..."
            rows={1}
            className="er-composer-input"
            style={{
              width: "100%",
              boxSizing: "border-box",
              lineHeight: `${LINE_HEIGHT_PX}px`,
              padding: "6px 4px",
            }}
          />
          {/* EFISSUE-830 / relay-66 — anchored mention/tag dropdowns.
              Mutually exclusive: only one renders at a time. Self-close
              when the parsed region disappears (whitespace, escape,
              cursor moves away). Slash commands take priority. */}
          {slashCmd && (
            <SlashCommandPicker
              query={slashCmd.query}
              onSelect={applySlashCommand}
              onClose={() => setSlashCmd(null)}
            />
          )}
          {mention && !slashCmd && (
            <MentionPicker
              participants={participants}
              query={mention.query}
              onSelect={applyMention}
              onClose={() => setMention(null)}
            />
          )}
          {tagMention && !mention && !slashCmd && (
            <TagPicker
              recents={recentTags}
              workspaceId={workspaceId}
              query={tagMention.query}
              onSelect={applyTagPick}
              isHumanLead={isHumanLead}
              onClose={() => setTagMention(null)}
            />
          )}
        </div>

        <button
          type="button"
          onClick={handleSend}
          disabled={!canSend}
          style={{
            background: canSend ? "var(--orange)" : "var(--paper-2)",
            color: canSend ? "var(--paper)" : "var(--ink-muted)",
            border: "none",
            borderRadius: "var(--radius-pill)",
            padding: "6px 16px",
            fontSize: 13,
            fontWeight: 600,
            cursor: canSend ? "pointer" : "not-allowed",
          }}
        >
          {sending ? "Sending…" : "Send"}
        </button>
      </div>

      {/* Hints + errors */}
      <div className="er-composer-meta">
        {error ? (
          <span style={{ color: "var(--tag-blocker)" }}>{error}</span>
        ) : parseRelayCommand(content) ? (
          <span style={{ color: "var(--accent)" }}>
            @relay {parseRelayCommand(content)!.verb} · will auto-tag relay:command
          </span>
        ) : (
          <span>Shift+Enter to send · Enter for new line · Posting as <strong>{author}</strong></span>
        )}
      </div>
    </div>
  );
}
